import React, { useEffect, useState } from 'react';
import { Badge, Col, Row, TabContent, TabPane, Table } from 'reactstrap';
import { toast } from 'react-toastify';
import PencilOutlineIcon from 'mdi-react/PencilOutlineIcon';
import Api from '../../../util/api';
import Loading from '../../../shared/components/custom/Loading';
import FormCard from '../../../shared/components/custom/FormCard';
import SquareIconButton from '../../../shared/components/custom/SquareIconButton';
import NavTab from '../../../shared/components/custom/NavTab';
import LazyTabPane from '../../../shared/components/custom/LazyTabPane';
import { partial } from '../../../util/translation/translation';
import ConnectFeeModal, { feesFromValues } from './ConnectFeeModal';
import ConnectReport from './ConnectReport';
import ConnectChecks from './ConnectChecks';
import {
  STATUS_COLORS,
  fetchClubNames,
  formatReceiverFees,
  receiverStatus,
} from '../connect';

const p = partial('connect');

// Super-users (a tab in Club administration): every club's receivers, and
// the platform's fee on each (a receiver takes no Connect payments until its
// fee is set); the report of all Connect payments; and the checks against
// Stripe.
const ConnectAllClubs = () => {
  const [tab, setTab] = useState('1');
  const [receivers, setReceivers] = useState([]);
  const [clubNames, setClubNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const fetchReceivers = async () => {
    setLoading(true);
    try {
      const [allReceivers, names] = await Promise.all([
        Api.connect.getAllReceivers(),
        fetchClubNames(),
      ]);
      setReceivers(allReceivers);
      setClubNames(names);
    } catch (err) {
      toast.error(
        typeof err === 'string' && err ? err : p('receiversRetrievingERROR')
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReceivers();
  }, []);

  const clubName = (teamId) => clubNames[`${teamId}`] || `#${teamId}`;

  // The standard fee, then each kind of product whose own fee changed (set,
  // or cleared back to the standard fee)
  const onSaveFee = async (values) => {
    try {
      const { fee, productFees } = feesFromValues(values);
      let receiver = await Api.connect.setReceiverFee(editing.id, fee);
      const before = editing.productFees || {};
      for (const [type, own] of Object.entries(productFees)) {
        const was = before[type] || null;
        if (own && JSON.stringify(own) !== JSON.stringify(was)) {
          receiver = await Api.connect.setReceiverProductFee(
            editing.id,
            type,
            own
          );
        } else if (!own && was) {
          receiver = await Api.connect.clearReceiverProductFee(
            editing.id,
            type
          );
        }
      }
      setReceivers((current) =>
        current.map((item) => (item.id === receiver.id ? receiver : item))
      );
      setEditing(null);
      toast.success(p('feeSaved'));
    } catch (err) {
      toast.error(typeof err === 'string' && err ? err : p('feeSaveERROR'));
    }
  };

  const receiverNames = Object.fromEntries(
    receivers.map((receiver) => [
      receiver.id,
      `${receiver.name} (${clubName(receiver.teamId)})`,
    ])
  );

  const waitingForFee = receivers.filter(
    (receiver) => receiverStatus(receiver) === 'feeNotSet'
  ).length;

  return (
    <>
      <Loading loading={loading} />
      <ConnectFeeModal
        receiver={editing}
        clubName={editing ? clubName(editing.teamId) : ''}
        onClose={() => setEditing(null)}
        onSubmit={onSaveFee}
      />
      <Row>
        <Col md={12}>
          <p className="mb-3">{p('connectFeesIntro')}</p>
        </Col>
      </Row>
      {/* Its own query param: Club administration's tabs use ?tab= */}
      <NavTab
        activeTab={tab}
        tabs={[p('connectFees'), p('report'), p('checks')]}
        setTab={setTab}
        queryName="connectTab"
        size="sm"
      />
      <TabContent activeTab={tab}>
        <TabPane tabId="1">
          <Row>
            <Col md={12}>
              <FormCard
                title={p('receiversCount', {
                  count: receivers.length,
                  waiting: waitingForFee,
                })}
                bodyClassName="form-card__body--flush"
              >
                <Table responsive striped className="table--roomy table--stack">
                  <thead>
                    <tr>
                      <th>{p('club')}</th>
                      <th>{p('receiver')}</th>
                      <th>{p('country')}</th>
                      <th>{p('status')}</th>
                      <th>{p('fee')}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {receivers.map((receiver) => {
                      const status = receiverStatus(receiver);
                      return (
                        <tr key={receiver.id}>
                          <td>
                            {clubName(receiver.teamId)}
                            <div className="text-muted">#{receiver.teamId}</div>
                          </td>
                          <td>
                            {receiver.name}
                            {receiver.isClubDefault && (
                              <Badge color="light" className="ml-2">
                                {p('clubDefault')}
                              </Badge>
                            )}
                            {receiver.stripeAccountId && (
                              <div className="text-muted">
                                {receiver.stripeAccountId}
                              </div>
                            )}
                          </td>
                          <td>
                            {receiver.country} ({receiver.currency})
                          </td>
                          <td>
                            <Badge color={STATUS_COLORS[status]}>
                              {p(`status-${status}`)}
                            </Badge>
                          </td>
                          <td>
                            {formatReceiverFees(receiver) || p('feeNotSet')}
                          </td>
                          <td>
                            <div className="icon-actions">
                              <SquareIconButton
                                icon={<PencilOutlineIcon size={16} />}
                                title={p('setFee')}
                                onClick={() => setEditing(receiver)}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {!loading && receivers.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center text-muted">
                          {p('noReceivers')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </FormCard>
            </Col>
          </Row>
        </TabPane>
        <LazyTabPane activeTab={tab} tabId="2">
          <ConnectReport all clubNames={clubNames} />
        </LazyTabPane>
        <LazyTabPane activeTab={tab} tabId="3">
          <ConnectChecks receiverNames={receiverNames} />
        </LazyTabPane>
      </TabContent>
    </>
  );
};

export default ConnectAllClubs;
